import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, FileText, Heart, Star } from 'lucide-react';

export default function ServicesPage() {
  return (
    <div className="page-wrapper" data-testid="services-page">
      {/* Hero Section */}
      <section className="hero-section relative min-h-[60vh] flex items-center overflow-hidden" data-testid="services-hero">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_cf0aab81-969a-4e9c-9793-f40790513670/artifacts/ozk0drws_IMAGE_PAGE_GARDE_CONSOLIDATION.png')`,
            opacity: 0.7
          }} />

        <div className="hero-overlay" />
        <div className="hero-content w-full pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl">

              <p className="text-[#C5A059] text-xs tracking-[0.3em] uppercase mb-4">
                Nos services
              </p>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#FDFBF7] font-medium tracking-tight leading-tight mb-6">
                Un accompagnement<br />
                <span className="text-gold-gradient">sur mesure</span>
              </h1>
              <div className="gold-line mb-8" />
              <p className="text-[#FDFBF7]/70 text-lg md:text-xl leading-relaxed">
                Des solutions personnalisées pour répondre à tous vos besoins patrimoniaux et personnels.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service 1 - Consolidation */}
      <section className="py-24 md:py-32 bg-[#FDFBF7]" data-testid="services-detail">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-32"
            data-testid="service-detail-consolidation">

            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 flex items-center justify-center border border-[#C5A059]/30">
                  <BarChart3 size={24} className="text-[#C5A059]" strokeWidth={1.5} />
                </div>
                <p className="text-[#C5A059] text-xs tracking-[0.3em] uppercase">Vision Globale</p>
              </div>
              <h2 className="md:text-4xl !font-serif !font-medium !text-3xl mb-6 text-[#0B1221]">Consolidation des actifs

              </h2>
              <div className="gold-line mb-8" />
              <p className="text-[#64748B] text-lg leading-relaxed mb-8">
                Nous centralisons et consolidons l'ensemble de vos actifs pour vous offrir une vision claire et exhaustive de votre patrimoine.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] mt-2 flex-shrink-0" />
                  <span className="text-[#64748B]">Reporting patrimonial régulier personnalisé</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] mt-2 flex-shrink-0" />
                  <span className="text-[#64748B]">Consolidation multi-devises</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] mt-2 flex-shrink-0" />
                  <span className="text-[#64748B]">Identification des performances</span>
                </li>
              </ul>
            </div>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border border-[#C5A059]/20" />
              <div className="bg-[#0B1221] p-12 md:p-16 relative">
                <div className="text-center">
                  <BarChart3 size={64} className="text-[#C5A059] mx-auto mb-6" strokeWidth={1} />
                  <p className="font-serif text-2xl text-[#FDFBF7] mb-2">Consolidation</p>
                  <p className="text-[#C5A059] text-xs tracking-[0.2em] uppercase">Vision Globale</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Service 2 - Transmission */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-32"
            data-testid="service-detail-transmission">

            <div className="lg:order-2">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 flex items-center justify-center border border-[#C5A059]/30">
                  <Heart size={24} className="text-[#C5A059]" strokeWidth={1.5} />
                </div>
                <p className="text-[#C5A059] text-xs tracking-[0.3em] uppercase">Planification Successorale</p>
              </div>
              <h2 className="md:text-4xl !font-serif !font-medium !text-3xl mb-6 text-[#0B1221]">Accompagnement à la transmission

              </h2>
              <div className="gold-line mb-8" />
              <p className="!text-lg mb-8 text-[#64748B]">Nous vous accompagnons dans la réflexion et la mise en oeuvre de votre stratégie de transmission patrimoniale de concert avec votre notaire/avocat.

              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] mt-2 flex-shrink-0" />
                  <span className="text-[#64748B]">Audit complet</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] mt-2 flex-shrink-0" />
                  <span className="text-[#64748B]">Optimisation transfrontalière</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] mt-2 flex-shrink-0" />
                  <span className="text-[#64748B]">Gouvernance familiale</span>
                </li>
              </ul>
            </div>
            <div className="lg:order-1 relative">
              <div className="absolute -top-4 -left-4 w-full h-full border border-[#C5A059]/20" />
              <div className="bg-[#0B1221] p-12 md:p-16 relative">
                <div className="text-center">
                  <Heart size={64} className="text-[#C5A059] mx-auto mb-6" strokeWidth={1} />
                  <p className="font-serif text-2xl text-[#FDFBF7] mb-2">Transmission</p>
                  <p className="text-[#C5A059] text-xs tracking-[0.2em] uppercase">Planification</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Service 3 - Administratif */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-32"
            data-testid="service-detail-administratif">

            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 flex items-center justify-center border border-[#C5A059]/30">
                  <FileText size={24} className="text-[#C5A059]" strokeWidth={1.5} />
                </div>
                <p className="text-[#C5A059] text-xs tracking-[0.3em] uppercase">Gestion Quotidienne</p>
              </div>
              <h2 className="md:text-4xl !font-serif !font-medium !text-3xl mb-6 text-[#0B1221]">Suivi administratif et financier

              </h2>
              <div className="gold-line mb-8" />
              <p className="!text-lg mb-8 text-[#64748B]">Nous prenons en charge la gestion administrative et financière de vos affaires courantes.

              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] mt-2 flex-shrink-0" />
                  <span className="text-[#64748B]">Coordination avec vos conseils habituels (notaire, avocat, banquiers, ...)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] mt-2 flex-shrink-0" />
                  <span className="text-[#64748B]">Suivi, préparation et coordination avec votre/vos expert(s)-comptable(s)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] mt-2 flex-shrink-0" />
                  <span className="text-[#64748B]">Archivage et documentation</span>
                </li>
              </ul>
            </div>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border border-[#C5A059]/20" />
              <div className="bg-[#0B1221] p-12 md:p-16 relative">
                <div className="text-center">
                  <FileText size={64} className="text-[#C5A059] mx-auto mb-6" strokeWidth={1} />
                  <p className="font-serif text-2xl text-[#FDFBF7] mb-2">Suivi</p>
                  <p className="text-[#C5A059] text-xs tracking-[0.2em] uppercase">Gestion</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Service 4 - Conciergerie */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center"
            data-testid="service-detail-conciergerie">

            <div className="lg:order-2">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 flex items-center justify-center border border-[#C5A059]/30">
                  <Star size={24} className="text-[#C5A059]" strokeWidth={1.5} />
                </div>
                <p className="text-[#C5A059] text-xs tracking-[0.3em] uppercase">Services Exclusifs</p>
              </div>
              <h2 className="md:text-4xl !font-serif !font-medium !text-3xl mb-6 text-[#0B1221]">Conciergerie privée

              </h2>
              <div className="gold-line mb-8" />
              <p className="!text-lg mb-8 text-[#64748B]">Un service sur mesure pour répondre à vos demandes les plus exigeantes dans votre vie quotidienne.

              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] mt-2 flex-shrink-0" />
                  <span className="text-[#64748B]">Recherche de biens d'exception</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] mt-2 flex-shrink-0" />
                  <span className="text-[#64748B]">Organisation de voyages privés</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059] mt-2 flex-shrink-0" />
                  <span className="text-[#64748B]">Assistance personnelle dédiée</span>
                </li>
              </ul>
            </div>
            <div className="lg:order-1 relative">
              <div className="absolute -top-4 -left-4 w-full h-full border border-[#C5A059]/20" />
              <div className="bg-[#0B1221] p-12 md:p-16 relative">
                <div className="text-center">
                  <Star size={64} className="text-[#C5A059] mx-auto mb-6" strokeWidth={1} />
                  <p className="font-serif text-2xl text-[#FDFBF7] mb-2">Conciergerie</p>
                  <p className="text-[#C5A059] text-xs tracking-[0.2em] uppercase">Exclusif</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-24 md:py-32 bg-[#F5F2EB]" data-testid="approach-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto">

            <p className="text-[#C5A059] text-xs tracking-[0.3em] uppercase mb-4">Notre Approche</p>
            <h2 className="md:text-4xl lg:text-5xl !font-serif !font-medium !text-3xl mb-6 text-[#0B1221]">Une méthodologie éprouvée

            </h2>
            <div className="gold-line mx-auto mb-8" />
            <p className="!text-lg text-[#64748B]">Chaque mission débute par une phase d'écoute approfondie pour comprendre vos objectifs, votre situation et vos contraintes.

            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center">

              <p className="font-serif text-5xl text-[#C5A059]/30 mb-4">01</p>
              <h3 className="font-serif text-2xl text-[#0B1221] mb-3">Écoute</h3>
              <p className="text-[#64748B] text-sm">Compréhension de vos objectifs</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-center">

              <p className="font-serif text-5xl text-[#C5A059]/30 mb-4">02</p>
              <h3 className="font-serif text-2xl text-[#0B1221] mb-3">Analyse</h3>
              <p className="text-[#64748B] text-sm">Diagnostic complet</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center">

              <p className="font-serif text-5xl text-[#C5A059]/30 mb-4">03</p>
              <h3 className="font-serif text-2xl text-[#0B1221] mb-3">Action</h3>
              <p className="text-[#64748B] text-sm">Mise en oeuvre</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0B1221]" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}>

            <p className="uppercase !text-xs !tracking-[0.3em] mb-4 text-[#C5A059]">Rencontrons-nous</p>
            <h2 className="md:text-4xl lg:text-5xl !font-serif !font-medium !text-3xl mb-6 text-[#FDFBF7]">Prenons le temps d'échanger

            </h2>
            <p className="max-w-xl !text-lg mx-auto mb-10 text-[#FDFBF7]/70">Chaque situation est unique. Contactez-nous pour un premier échange confidentiel.

            </p>
            <Link to="/contact" className="btn-gold inline-flex items-center gap-3" data-testid="services-cta-button">
              Nous Contacter
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>);

}